import { useCallback, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { useApiOperation } from "src/api/hooks";
import { getEmailExistsAsync } from "src/api/member/member-api";
import { BaseButton, BaseTextInput } from "src/components";
import Header from "src/components/layout/Header";
import MetaTag from "src/components/layout/MetaTag";
import { isJustLoggedInState, pendingJoinEmailState } from "src/recoil/auth/atom";
import { useModal } from "src/recoil/modalState/hook";
import { convertToPartnerUrl } from "src/utils/common-util";
type FormData = {
  email: string;
};

/**
 * 이메일 등록 화면 (회원가입)
 */
const RegisterEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { setBaseModal } = useModal();
  const setIsJustLoggedIn = useSetRecoilState(isJustLoggedInState);
  const setPendingJoinEmail = useSetRecoilState(pendingJoinEmailState);
  const { executeAsync: checkEmailExists, loading: checkingEmail } = useApiOperation(getEmailExistsAsync, { noHandleError: true });

  const {
    handleSubmit,
    register,
    control,
    setError,
    formState: { isValid, isDirty },
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      email: "",
    },
  });

  useEffect(() => {
    register("email", {
      required: "이메일을 입력해주세요",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "올바르지 않은 이메일 형식입니다",
      },
    });
  }, [register]);

  useEffect(() => {
    setIsJustLoggedIn(false);
  }, [setIsJustLoggedIn]);

  // 이메일을 보관하고 약관 페이지로 이동 (STARTER => MEMBER 처리 + 약관 동의는 약관 페이지에서 일괄 수행)
  const join = useCallback(
    async (data: FormData) => {
      // 이메일 중복 사전 확인 (약관 도달 전에 차단). 중복은 공통 컨펌 모달, 확인 실패는 하단 안내로 막는다(fail-safe).
      const res = await checkEmailExists({ email: data.email });
      const info = res?.data?.data;
      if (info?.exists) {
        setBaseModal({
          isOpen: true,
          title: "이미 사용중인 이메일 입니다.\n이메일로 로그인 해주세요.",
          btnRightTitle: "확인",
          onClick: () => setBaseModal({ isOpen: false }),
          onClose: () => setBaseModal({ isOpen: false }),
        });
        return;
      }
      if (!info) {
        setError("email", { type: "checkFailed", message: "이메일 확인에 실패했습니다. 다시 시도해 주세요." });
        return;
      }
      setPendingJoinEmail(data.email);
      navigate(convertToPartnerUrl(location.pathname, "/court/join/agreements"), { replace: true });
    },
    [checkEmailExists, setError, setBaseModal, setPendingJoinEmail, navigate, location.pathname],
  );

  // validation 통과 후 submit 될때 실행
  const onSubmit = useCallback(
    (data: FormData, e?: any) => {
      e.preventDefault();

      console.log("onSubmit", data, e);

      // 회원가입
      join(data);
    },
    [join],
  );

  // validation 통과하지 못하고 error 발생시 실행
  const onError = (errors: any, e?: any) => {
    console.log("onError errors", errors);
    return false;
  };

  return (
    <>
      <Header headerType={"BACK"} />
      <div className="my-page">
        <MetaTag title="회원가입 | TaapSpace" />

        <article className="container">
          <form onSubmit={handleSubmit(onSubmit, onError)}>
            <div className="title-wrap">
              <h5>이메일을 입력해 주세요</h5>
              <p className="description">
                휴대폰 번호 변경 시 본인 확인을 위해 이메일을 입력해 주세요.
                <br /> <span className="text-red font13">*기업 사용자는 기업 이메일을 입력해 주세요</span>
              </p>
            </div>

            {/* 약관동의 */}

            <Controller
              control={control}
              name="email"
              render={({ field: { name, value, onChange }, fieldState: { error, isDirty } }) => (
                <BaseTextInput
                  name={name}
                  value={value}
                  onChange={onChange}
                  placeholder="이메일을 입력해주세요(필수)"
                  isInvalid={!!error}
                  invalidMessage={error?.message}
                  isCompleted={isDirty && !error}
                />
              )}
            ></Controller>

            <BaseButton type="submit" className="login-btn join-email-submit" disabled={!isDirty || !isValid} title="다음" isLoading={checkingEmail} />
          </form>
        </article>
      </div>
    </>
  );
};
export default RegisterEmail;
